import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { SquareLinkedService, squareReservedKeys } from 'app/model/linkedservice/square.linkedservice';
import { Metamodel } from 'app/model/base/model';
import { SquareCopySource } from 'app/model/pipeline/copysource/square.copysource';
import { useEncryptedEndpointsRadioSwitch } from './linked-service-shared-fields';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { driverVersionVersionOptions, driverVersions } from '../consts/tabular-consts';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';

const additionalFields: ControlMetadata = {
        type: 'key-val-list',
        bind: 'additionalFields',
        label: stringResources.AdditionalConnectionStringFields,
        hide: '{ isOldResource }',
        reservedKeys: squareReservedKeys
    }
const connectionForm: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'host',
        label: stringResources.ModelName_Host,
        required: true,
        placeholder: stringResources.Square_LinkedService_Host_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        warningMessageCalculator: (value: string) =>
            stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning,
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'clientId',
        label: stringResources.ModelName_ClientId,
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'clientSecret',
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'password',
            label: stringResources.ModelName_ClientSecret,
            isLabelVertical: true,
            required: true
        },
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Square_DisplayText),
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'sqaure-refresh-access-token',
        bind: {
            refreshToken: 'refreshToken',
            accessToken: 'accessToken'
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hide: ('{ isOldResource }' as any) as boolean // it's an expression and will be evaluated into boolean-type value in control factory service
    },
    {
        type: 'textbox',
        bind: 'redirectUri',
        label: stringResources.ModelName_RedirectUri,
        required: true,
        placeholder: stringResources.Square_LinkedService_RedirectUri_Placeholder,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        hide: ('{ !isOldResource }' as any) as boolean // it's an expression and will be evaluated into boolean-type value in control factory service
    }
]
export const squareMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(SquareLinkedService).name,
    displayName: stringResources.Square_DisplayText,
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-square'
    },
    isFirstClass: true,
    supportDatasetV2: true,
    category: ['ServicesApps'],
    copySinkTypes: [],
    isDeprecated: GlobalVariables.showLegacy ? undefined : '{ version !== "v2" }',
    showWarningMessage: '{ version !== "v2" }',
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt5,
        stringResources.VersionLabel.toLocaleLowerCase()),
    copySourceTypes: [Metamodel.modelFor(SquareCopySource).name],
    SHIRVersion: {
        v2: '5.56.0.0'
    },
    controls: [        
         {
            type: 'radio-switch',
            isLabelVertical: true,
            label: stringResources.VersionLabel,
            required: true,
            bind: 'version',
            options: driverVersionVersionOptions,
            hide: GlobalVariables.showLegacy ? false : '{version === "v2" }',
            defaultValue: driverVersions.v2,
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [
                        {
                            type: 'hint',
                            hintMode: 'warning',
                            description: format(
                                stringResources.Connector_Legacy_IsNotSupportedWarning_alt6,
                                stringResources.VersionLabel.toLocaleLowerCase()),
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-square'
                        },
                        ...connectionForm,
                        useEncryptedEndpointsRadioSwitch,
                        additionalFields
                    ]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [
                        ...connectionForm
                    ]
                }
            ]
        },
        ...connectionForm.map((control) => {
            if (control.type === 'sqaure-refresh-access-token') {
                return {
                    ...control,
                    hide: GlobalVariables.showLegacy ? true : '{ isOldResource || version !== "v2" }'
                };
            }
            if (control.bind === 'redirectUri') {
                return {
                    ...control,
                    hide: GlobalVariables.showLegacy ? true : '{ !isOldResource || version !== "v2" }'
                };
            }
            return {
                ...control,
                hide: !GlobalVariables.showLegacy ? '{version !== "v2"}' : true
        };
        })
    ]
};
